import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { TrendingUp, Home, Store, Briefcase, User, GraduationCap, Clock, Heart, Bitcoin, Wheat, Award, Milk } from 'lucide-react';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { CompactVideoStrip } from '../components/v2/CompactVideoStrip';
import { ZAKAT_VIDEOS } from '../config/educationalVideos';
import { hp } from '../config/homepageStrings';
// HowToGuide import removed — the wizard + How-To lives on /zakat/calculator now.
import { usePageMeta } from '../hooks/usePageMeta';
import { FAQSection } from '../components/FAQSection';
import { HadithBanner, HADITHS } from '../components/HadithBanner';
// (CountryRulesPill no longer imported here — the prominent CountryLiftCard
// rendered above the calculator surfaces the per-country legal rules link
// instead. The smaller pill remains in use on /will and /inheritance pages.)
import { PageHeaderShareRow } from '../components/PageHeaderShareRow';
import LocalizedShareRow from '../components/LocalizedShareRow';
import { ZakatFooter } from '../components/zakat/ZakatFooter';
import { PosterThumbStrip } from '../components/v2/PosterThumbStrip';
import { ZakatHero } from '../components/zakat/ZakatHero';
import HeroBento from '../components/v2/HeroBento';
import ProductCardsV2 from '../components/v2/ProductCardsV2';
import { PrayerCompass } from '../components/PrayerCompass';
import UnderstandBeforeYouCalculate from '../components/v2/UnderstandBeforeYouCalculate';
import { AnswerBox } from '../components/v2/AnswerBox';
import { KitDownloadButton } from '../components/KitDownloadButton';
import { MasjidPosterButton } from '../components/MasjidPosterButton';
import { HowToInfographic } from '../components/HowToInfographic';
import { SiteHeader } from '../components/SiteHeader';
import { isAppBuild } from '../utils/buildTarget';

// Icon mapping
const iconMap = {
  Briefcase, User, Store, TrendingUp, Wheat, Clock, GraduationCap,
  Heart, Home, Bitcoin, Milk, Award
};

// Single mobile-menu row. Lives next to the nav so it can reuse the
// already-imported lucide icons without another import block.
const ZakatGlobalPage = () => {
  const navigate = useNavigate();
  const {
    selectedCountry, selectedLanguage, selectedPersonType, selectedPersonTypes, currentStep,
    nisabStandard, goldPrice, silverPrice, effectiveGoldPrice, effectiveSilverPrice,
    pricesLoading, pricesError, priceSource, priceLastUpdated,
    assetValues, deductionValues, countries, languages,
    useManualRates, manualGoldPrice, manualSilverPrice, goldVariation, silverVariation,
    updateCountry, updateLanguage, setSelectedPersonType, togglePersonType, setCurrentStep,
    setNisabStandard, updateAssetValue, updateDeductionValue,
    setUseManualRates, setManualGoldPrice, setManualSilverPrice,
    calculateZakat, resetCalculator, formatCurrency, t, nisabConstants,
    getAvailableAssetFields, getAvailableDeductionFields, fetchMetalPrices
  } = useZakatGlobal();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [smartAnswers, setSmartAnswers] = useState({}); // e.g. { stocks: 'trading', crypto: 'holding' }
  const [showScholarView, setShowScholarView] = useState(false);
  const [expandedTooltip, setExpandedTooltip] = useState(null);

  // SEO + AEO — per-route metadata. Country/language counts are derived
  // from the live arrays so they auto-update with each round of additions.
  usePageMeta({
    title: `Amanah by Zakat.International — Free Zakat, Islamic Inheritance (Faraid) & Will Calculator · ${countries.length} Countries · ${languages.length} Languages`,
    description: `Free Zakat, Faraid & Islamic Will toolkit. ${countries.length} countries, ${languages.length} languages, live Nisab, privacy-first — your data stays in your browser.`,
    keywords: 'Islam, Muslim, Muslim family, inheritance in Islam, Islamic inheritance, partition of estate in Islam, death and bequest in Islam, children rights in Islam, women inheritance Islam, Quranic shares, Faraid, Mirath, Wasiyyah, Islamic will, Sharia will, Zakat calculator, Nisab, gold Nisab, silver Nisab, Hanafi Maliki Shafii Hanbali Jafari, mahr, orphaned grandchildren, Wajib Wasiyyah',
    canonical: 'https://zakat.international/',
    lang: selectedLanguage?.code || 'en',
    dir: selectedLanguage?.dir || 'ltr',
    ogImage: 'https://zakat.international/og-cover.png',
    hreflangPath: '/',
    breadcrumbs: [{ name: 'Home', url: '/' }],
  });

  // (Removed) sticky-CTA scroll listener — the top sticky CTA bar was
  // dropped because the bottom MobileBottomNav already surfaces Zakat /
  // Faraid / Will on mobile, and the desktop top-nav pills do the same on
  // larger screens.

  const isRTL = selectedLanguage.dir === 'rtl';
  const lang = selectedLanguage?.code || 'en';

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    setMobileMenuOpen(false);
  };

  // (P0 Homepage restructure) — goToStep + result-share handlers moved to
  // /app/frontend/src/pages/ZakatCalculatorPage.js along with the full
  // CalculatorCard flow. The homepage no longer renders the wizard.

  return (
    <div className={`gd-page min-h-screen premium-touch ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Single-row site header — see SiteHeader.jsx for details */}
      <SiteHeader onOpenFaq={() => scrollToSection('learn')} />

      {/* Push content below the fixed ~48px header */}
      <div className="pt-10 md:pt-12">

      {/* Hero — H1 + subtitle + inline QuickZakatCalculator so the tool is
          the FIRST interactive surface (no navigation choice required). */}
      <HeroBento showQuickCalc={true} />

      {/* ─────────────────────────  TOOL ROW  ───────────────────────── */}
      <section
        id="calculators"
        data-testid="section-calculators"
        className="page-section scroll-mt-[80px] py-10 md:py-14 border-b border-stone-200"
      >
        <ProductCardsV2 />
      </section>

      {/* Educational primer — surfaces existing Learning Library content
          with scope chips so visitors understand each pillar before they
          enter the calculator. No new fiqh / legal claims are made here;
          every substantive detail is linked out to a named source in
          `learningResources.js`. */}
      <UnderstandBeforeYouCalculate testid="understand-before-calc" />

      {/* Prayer Compass — small Qibla + prayer-time widget. Fully
          client-side (zero network calls). Encourages returning users
          to install the PWA so these glance-reads stay one tap away. */}
      <section
        id="prayer-compass"
        data-testid="section-prayer-compass"
        aria-label="Qibla direction and today's prayer times"
        className="page-section scroll-mt-[80px] py-8 md:py-10 border-b border-stone-200 bg-[#FAF8F5]"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <PrayerCompass testid="home-prayer-compass" />
        </div>
      </section>

      {/* ─────────  COLLAPSIBLE STACK (all closed by default)  ───────── */}
      <section
        id="home-more"
        data-testid="section-home-more"
        className="gd-anchor page-section scroll-mt-[80px] py-8 md:py-12 border-b border-[#CBD2D9] bg-[#EDF5F1]"
      >
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Accordion type="multiple" className="w-full space-y-2" data-testid="home-accordion">
            <AccordionItem value="tldr" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
              <AccordionTrigger data-testid="home-acc-tldr" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                How is Zakat calculated in 2026?
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <AnswerBox
                  testid="zakat-tldr"
                  question={hp(lang, 'tldrQuestion')}
                  answer={<>{hp(lang, 'tldrAnswer')}</>}
                  points={[
                    { label: hp(lang, 'labelRate'),         value: hp(lang, 'valueRate') },
                    { label: hp(lang, 'labelNisabSilver'),  value: '~USD 470 · 595 g' },
                    { label: hp(lang, 'labelNisabGold'),    value: '~USD 7,000 · 85 g' },
                    { label: hp(lang, 'labelHawl'),         value: hp(lang, 'valueHawl') },
                    { label: hp(lang, 'labelWhatCounts'),   value: hp(lang, 'valueWhatCounts') },
                    { label: hp(lang, 'labelWhatDoesnt'),   value: hp(lang, 'valueWhatDoesnt') },
                  ]}
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="hadith" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
              <AccordionTrigger data-testid="home-acc-hadith" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                Hadith of the Prophet ﷺ on Zakat
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <HadithBanner
                  arabic={HADITHS.zakat.arabic}
                  english={HADITHS.zakat.english}
                  citation={HADITHS.zakat.citation}
                  testid="zakat-hadith"
                  useRegional
                />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="faq" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
              <AccordionTrigger data-testid="home-acc-faq" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                Knowledge centre &mdash; frequently asked questions
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <FAQSection testid="zakat-faq" defaultOpen={null} />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="videos" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
              <AccordionTrigger data-testid="home-acc-videos" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                Video lessons
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5">
                <CompactVideoStrip
                  title={hp(lang, 'learnZakatHeading')}
                  subtitle={hp(lang, 'learnZakatSubheading')}
                  videos={ZAKAT_VIDEOS}
                  testid="home-compact-videos"
                  ctaLabel="See all"
                  onCtaClick={() => navigate('/learn')}
                />
              </AccordionContent>
            </AccordionItem>

            {!isAppBuild && (
              <AccordionItem value="kit" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
                <AccordionTrigger data-testid="home-acc-kit" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                  Estate planning kit &amp; masjid posters
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <KitDownloadButton variant="primary" testid="home-kit-download" />
                    <MasjidPosterButton variant="primary" testid="home-masjid-poster" />
                  </div>
                  <HowToInfographic
                    src="/infographics/kit.png"
                    alt="What's inside the Free Islamic Estate Planning Kit — 8-template illustrated overview"
                    caption="What's in the kit · Tap to enlarge"
                    testid="home-kit-infographic"
                  />
                  <PosterThumbStrip
                    title="Knowledge posters"
                    subtitle="Tap a thumbnail to view, download or share."
                    testid="home-poster-strip-bottom"
                  />
                </AccordionContent>
              </AccordionItem>
            )}

            {!isAppBuild && (
              <AccordionItem value="share" className="border border-stone-200 rounded-2xl bg-white overflow-hidden">
                <AccordionTrigger data-testid="home-acc-share" className="px-5 py-4 text-left font-heading text-[16px] md:text-[18px] text-[#0D1520] hover:no-underline">
                  Share &amp; invite
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <PageHeaderShareRow
                    testid="zakat-share"
                    title="Personal Zakat Calculator — Amanah by Zakat.International"
                    text="Free Zakat calculator with live nisab in 53 currencies, all 4 Sunni schools. Saved only in your browser."
                    compact
                    rightSlot={
                      <LocalizedShareRow
                        selectedCountry={selectedCountry}
                        selectedLanguage={selectedLanguage}
                        testid="zakat-localized-share"
                      />
                    }
                  />
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </section>

      {/* Legacy Hero preserved further down (retained for existing SEO copy + salaam block).
          TODO: retire once V2 content parity is verified. */}
      <div className="hidden">
        <ZakatHero
          t={t}
          selectedLanguage={selectedLanguage}
          onStartCalculator={() => scrollToSection('person-types')}
          onScrollToRulings={() => scrollToSection('rulings')}
          onScrollToFaq={() => scrollToSection('learn')}
          onOpenInheritance={() => navigate('/inheritance')}
          onOpenWill={() => navigate('/will')}
          onOpenWalkthrough={() => navigate('/walkthrough')}
          onOpenBusiness={() => navigate('/business-calculator')}
        />
      </div>

      {/* Footer */}
      <ZakatFooter scrollToSection={scrollToSection} />

      </div>{/* /pt-16 wrapper */}

      <MobileBottomNav />
    </div>
  );
};

export default ZakatGlobalPage;
